import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { API_BASE, BUSINESS } from "@/lib/data";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, data: null, error: "Missing session" });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ loading: false, data, error: null });
          return;
        }
      } catch (e) {
        if (attempts > 8) {
          setState({ loading: false, data: null, error: "Could not confirm payment" });
          return;
        }
      }
      attempts += 1;
      if (attempts <= 15) setTimeout(poll, 2000);
      else setState({ loading: false, data: null, error: "Payment confirmation is taking longer than expected" });
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-obsidian flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-surface border border-neutral-800 rounded-sm p-10 text-center">
        <div className="flex justify-center mb-6">
          <img src="/images/logo-mark.png" alt="Teign Gas" className="h-12 w-auto mix-blend-screen" />
        </div>
        {state.loading && (
          <>
            <Loader2 className="w-8 h-8 text-torch animate-spin mx-auto mb-4" />
            <h1 className="font-display font-bold text-xl text-white uppercase">Confirming your payment…</h1>
            <p className="text-sm text-neutral-400 mt-3">Hold tight, this takes a few seconds.</p>
          </>
        )}
        {!state.loading && state.data && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h1 className="font-display font-extrabold text-2xl text-white uppercase tracking-tight">Booking confirmed</h1>
            {state.data.booking && (
              <div data-testid="booking-confirmation" className="mt-6 border border-neutral-800 rounded-sm p-5 text-left space-y-2">
                <p className="font-mono text-xs text-torch tracking-[0.2em] uppercase">Ref {state.data.booking.ref}</p>
                <p className="text-white font-semibold">{state.data.booking.service_name}</p>
                <p className="text-sm text-neutral-400">{state.data.booking.date} · {state.data.booking.slot}</p>
              </div>
            )}
            <p className="text-sm text-neutral-400 mt-5">
              We've emailed your receipt. If anything changes, call us on{" "}
              <a href={BUSINESS.phoneHref} className="text-torch hover:underline">{BUSINESS.phone}</a>.
            </p>
            <Link
              to="/"
              data-testid="success-home-link"
              className="mt-7 inline-block bg-torch hover:bg-torch-hover text-white font-display font-bold uppercase tracking-wide px-7 py-3 rounded-sm transition-colors"
            >
              Back to site
            </Link>
            <p className="mt-5 text-sm text-neutral-500">
              Happy to recommend us?{" "}
              <a href={BUSINESS.google} target="_blank" rel="noopener noreferrer" data-testid="success-review-link" className="text-torch hover:underline">
                Leave us a Google review
              </a>{" "}
              — it really helps a local business.
            </p>
          </>
        )}
        {!state.loading && state.error && (
          <>
            <h1 className="font-display font-bold text-xl text-white uppercase">Almost there</h1>
            <p className="text-sm text-neutral-400 mt-3">{state.error}. If you completed payment, your booking is safe — call {BUSINESS.phone} and we'll confirm it.</p>
            <Link to="/" data-testid="success-home-link" className="mt-7 inline-block border border-neutral-700 hover:border-torch text-neutral-200 font-display font-bold uppercase tracking-wide px-7 py-3 rounded-sm transition-colors">
              Back to site
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
